/**
 * The turn log in words (docs/ux.md U-LOG-01): one entry per player action, built from the
 * engine's events, plus the storm's and the end of turn's consequences. Each entry knows the
 * cells it is about, so clicking it can point them out on the board (U-LOG-02).
 */
import type { Action, GameEvent, GameState, Side } from '@brig/rules-engine';
import type { TFunction } from 'i18next';
import type { History } from './history.ts';
import { unitLabel } from './names.ts';

export type LogTone = 'plain' | 'good' | 'bad';

/** What an entry is about, for its icon and the log's filters (spec v2 §16). */
export type LogKind = 'wind' | 'move' | 'fire' | 'hit' | 'repair' | 'capture' | 'turn' | 'game';

const KINDS: Readonly<Partial<Record<GameEvent['type'], LogKind>>> = {
  spun: 'wind',
  shipMoved: 'move',
  shipAnchored: 'move',
  shipWrecked: 'hit',
  shipSunk: 'hit',
  dockEntered: 'repair',
  shipRepaired: 'repair',
  dockLeft: 'repair',
  fortRepairStarted: 'repair',
  fortRepairStopped: 'repair',
  fortRepaired: 'repair',
  fortCaptured: 'capture',
  royalHold: 'capture',
};

export interface LogEntry {
  /** Stable while the history only grows: action index and line within it. */
  readonly key: string;
  /** The history state this entry leads to (for replays and highlights). */
  readonly state: number;
  readonly turn: number;
  readonly side: Side;
  readonly text: string;
  readonly cells: readonly string[];
  /** Good or bad news for the side that acted. */
  readonly tone: LogTone;
  readonly kind: LogKind;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

function cellOf(state: GameState, id: string): string | null {
  return state.ships.find((s) => s.id === id)?.cell ?? state.forts.find((f) => f.id === id)?.cell ?? null;
}

/** The sentences for one action's events. */
function lines(
  t: Translate,
  tf: TFunction,
  action: Action,
  events: readonly GameEvent[],
  before: GameState,
  after: GameState,
  nations: Readonly<Record<Side, string>>,
): { text: string; cells: string[]; tone: LogTone; kind: LogKind }[] {
  const name = (id: string) => unitLabel(tf, after, nations, id);
  const side = (s: Side) => after.players[s].name;
  const at = (...ids: string[]) => ids.map((id) => cellOf(after, id)).filter((c): c is string => c !== null);
  const out: { text: string; cells: string[]; tone: LogTone; kind: LogKind }[] = [];
  let kind: LogKind = 'game';
  const push = (text: string, cells: string[] = [], tone: LogTone = 'plain') => {
    out.push({ text, cells, tone, kind });
  };

  for (const e of events) {
    kind = e.type === 'shot' ? (e.hit ? 'hit' : 'fire') : (KINDS[e.type] ?? 'game');
    switch (e.type) {
      case 'spun':
        push(
          t('game.log.wind', {
            side: side(e.side),
            n: after.turn.group.length,
            weather: t(`game.weather.${e.result.weather}`),
            dir: t(`game.dir.${e.result.direction}`),
          }),
          at(...after.turn.group),
        );
        break;
      case 'shipMoved':
        if (e.mode === 'storm')
          push(t('game.log.drift', { ship: name(e.shipId), from: e.from, to: e.to }), [e.to]);
        else if (e.mode === 'port')
          push(t('game.log.inPort', { ship: name(e.shipId), to: e.to }), [e.from, e.to]);
        else push(t('game.log.sailed', { ship: name(e.shipId), from: e.from, to: e.to }), [e.from, e.to]);
        break;
      case 'shipAnchored':
        push(t('game.log.anchored', { ship: name(e.shipId), cell: e.cell }), [e.cell]);
        break;
      case 'shipWrecked':
        push(t('game.log.wrecked', { ship: name(e.shipId), cell: e.cell }), [e.cell], 'bad');
        break;
      case 'shot': {
        const target = e.targetId;
        const ship = after.ships.find((s) => s.id === target);
        const fort = after.forts.find((f) => f.id === target);
        let result: string;
        if (!e.hit) result = t('game.log.miss');
        else if (ship?.sunk) result = t('game.log.sunk');
        else if (ship)
          result = t('game.log.holes', { n: ship.damage, max: after.ruleset.shipClasses[ship.cls].sinksAt });
        else if (fort?.destroyed) result = t('game.log.destroyed');
        else if (fort)
          result = t('game.log.fortHits', {
            n: fort.hits,
            max: after.ruleset.fortClasses[fort.cls].destroyedAt,
          });
        else result = t('game.log.hit');
        const shots = e.shooters.reduce((n, s) => n + s.shots, 0);
        const cells = [
          ...at(...e.shooters.map((s) => s.id)),
          ...(cellOf(before, target) ? [cellOf(before, target) as string] : []),
        ];
        const text = e.volley
          ? t('game.log.volley', {
              n: shots,
              shooters: e.shooters.map((s) => name(s.id)).join(', '),
              target: name(target),
              result,
            })
          : t('game.log.shot', { shooter: name(e.shooters[0]?.id ?? ''), target: name(target), result });
        push(text, cells, e.hit ? 'good' : 'plain');
        break;
      }
      case 'shipSunk':
        if (e.cause === 'fort_destroyed')
          push(t('game.log.sunkInDock', { ship: name(e.shipId) }), at(e.shipId), 'bad');
        break;
      case 'dockEntered': {
        const ship = after.ships.find((s) => s.id === e.shipId);
        push(t('game.log.dock', { ship: name(e.shipId), n: ship?.damage ?? 0 }), at(e.shipId));
        break;
      }
      case 'shipRepaired':
        push(t('game.log.repaired', { ship: name(e.shipId), n: e.damage }), at(e.shipId), 'good');
        break;
      case 'dockLeft':
        push(t('game.log.dockLeft', { ship: name(e.shipId) }), at(e.shipId));
        break;
      case 'fortRepairStarted':
        push(t('game.log.fortRepair', { side: side(e.side), fort: name(e.fortId) }), at(e.fortId));
        break;
      case 'fortRepairStopped':
        push(t(`game.log.fortRepairStopped.${e.reason}`, { fort: name(e.fortId) }), at(e.fortId));
        break;
      case 'fortRepaired':
        push(t('game.log.fortRepaired', { fort: name(e.fortId), n: e.hits }), at(e.fortId), 'good');
        break;
      case 'fortCaptured':
        push(t('game.log.captured', { fort: name(e.fortId), side: side(e.side) }), at(e.fortId), 'good');
        break;
      case 'royalHold':
        push(
          e.count > 0
            ? t('game.log.royalHold', {
                side: side(e.side),
                n: e.count,
                max: after.ruleset.royalPortHoldTurns,
              })
            : t('game.log.royalLost', { side: side(e.side) }),
          [],
          e.count > 0 ? 'good' : 'bad',
        );
        break;
      case 'drawOffered':
        push(t('game.log.drawOffered', { side: side(e.side) }));
        break;
      case 'drawOfferExpired':
        push(t('game.log.drawExpired', { side: side(e.side) }));
        break;
      case 'resigned':
        push(t('game.log.resigned', { side: side(e.side) }));
        break;
      case 'gameEnded':
        push(
          e.status.kind === 'won' ? t('game.over.won', { side: side(e.status.winner) }) : t('game.over.draw'),
        );
        break;
      default:
        break;
    }
  }
  if (action.type === 'endTurn' && !events.some((e) => e.type === 'gameEnded')) {
    out.unshift({
      text: t('game.log.endTurn', { side: side(action.side) }),
      cells: [],
      tone: 'plain',
      kind: 'turn',
    });
  }
  if (action.type === 'acceptDraw')
    out.unshift({
      text: t('game.log.drawAccepted', { side: side(action.side) }),
      cells: [],
      tone: 'plain',
      kind: 'game',
    });
  return out;
}

/** Every entry of the game so far, oldest first. */
export function logEntries(t: TFunction, history: History): LogEntry[] {
  const translate = t as unknown as Translate;
  return history.actions.flatMap((action, i) => {
    const before = history.states[i] as GameState;
    const after = history.states[i + 1] as GameState;
    const events = history.events[i + 1] ?? [];
    return lines(translate, t, action, events, before, after, history.nations).map((line, j) => ({
      key: `${i}.${j}`,
      state: i + 1,
      turn: before.turn.number,
      side: before.turn.side,
      ...line,
    }));
  });
}
